import { useState, useMemo, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Plus, Upload, Download, Building2, Pencil, Trash2 } from "lucide-react";
import { useEmployees } from "../hooks";

// Import split components
import {
  EmployeeForm,
  EmployeeTable,
  EmployeeFilters,
  EmployeeStats,
  DepartmentDialog,
  DesignationDialog,
  DepartmentButton,
  DesignationButton,
  ImportDialog,
  ExportDialog,
  LoginDialog,
  ResetPasswordDialog,
} from "../components/employees";

const INITIAL_EMPLOYEE_FORM = {
  first_name: "", last_name: "", email: "", phone: "",
  department_id: "", designation_id: "", reports_to: "",
  is_department_head: false, salary: "", hire_date: "",
  birthday: "", biometric_id: "", employee_code: "",
  address: "", pin_code: "", father_or_husband_name: "",
  emergency_contact_person: "", emergency_contact_number: "",
  blood_group: "", work_location: "",
};

const Employees = () => {
  const { currentBusiness } = useAuth();
  
  // Use custom hook for data fetching
  const {
    employees, departments, designations, loading, stats,
    createEmployee, updateEmployee, deleteEmployee,
    createDepartment, updateDepartment, deleteDepartment,
    createDesignation, updateDesignation, deleteDesignation,
    importCSV, exportCSV, exportEmployees, downloadTemplate,
    createEmployeeLogin, resetEmployeePassword,
    getDepartmentName, getDesignationName,
  } = useEmployees(currentBusiness?.id);

  // Local UI state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [desgDialogOpen, setDesgDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);

  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDesgId, setEditingDesgId] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [exportFormat, setExportFormat] = useState("excel");
  const [exportStatus, setExportStatus] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  const [formData, setFormData] = useState(INITIAL_EMPLOYEE_FORM);
  const [deptFormData, setDeptFormData] = useState({ name: "", description: "", approver_id: "" });
  const [desgFormData, setDesgFormData] = useState({ name: "" });
  
  const [loginEmployee, setLoginEmployee] = useState(null);
  const [loginPassword, setLoginPassword] = useState("");
  const [creatingLogin, setCreatingLogin] = useState(false);
  
  const [resetPasswordEmployee, setResetPasswordEmployee] = useState(null);
  const [newResetPassword, setNewResetPassword] = useState("");
  const [resettingPassword, setResettingPassword] = useState(false);

  // Memoized filtered employees
  const filteredEmployees = useMemo(() => 
    employees.filter(emp =>
      `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emp.email.toLowerCase().includes(searchTerm.toLowerCase())
    ), [employees, searchTerm]
  );

  const resetForm = useCallback(() => {
    setFormData(INITIAL_EMPLOYEE_FORM);
    setEditingEmployee(null);
  }, []);

  // Employee handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        salary: parseFloat(formData.salary) || 0,
        reports_to: formData.reports_to || null,
      };

      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, payload);
        toast.success("Employee updated successfully");
      } else {
        await createEmployee(payload);
        toast.success("Employee added successfully");
      }
      
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      first_name: employee.first_name,
      last_name: employee.last_name,
      email: employee.email,
      phone: employee.phone || "",
      department_id: employee.department_id || "",
      designation_id: employee.designation_id || "",
      reports_to: employee.reports_to || "",
      is_department_head: employee.is_department_head || false,
      salary: employee.salary?.toString() || "",
      hire_date: employee.hire_date || "",
      birthday: employee.birthday || "",
      biometric_id: employee.biometric_id || "",
      employee_code: employee.employee_code || "",
      address: employee.address || "",
      pin_code: employee.pin_code || "",
      father_or_husband_name: employee.father_or_husband_name || "",
      emergency_contact_person: employee.emergency_contact_person || "",
      emergency_contact_number: employee.emergency_contact_number || "",
      blood_group: employee.blood_group || "",
      work_location: employee.work_location || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this employee?")) return;
    try {
      await deleteEmployee(id);
      toast.success("Employee deleted successfully");
    } catch {
      toast.error("Failed to delete employee");
    }
  };

  // Department handlers
  const handleCreateDepartment = async (e) => {
    e.preventDefault();
    try {
      if (editingDeptId) {
        await updateDepartment(editingDeptId, deptFormData);
        toast.success("Department updated");
      } else {
        await createDepartment(deptFormData);
        toast.success("Department created");
      }
      setDeptDialogOpen(false);
      setDeptFormData({ name: "", description: "", approver_id: "" });
      setEditingDeptId(null);
    } catch {
      toast.error("Failed to save department");
    }
  };

  const handleEditDepartment = (dept) => {
    setDeptFormData({ name: dept.name, description: dept.description || "", approver_id: dept.approver_id || "" });
    setEditingDeptId(dept.id);
    setDeptDialogOpen(true);
  };

  const handleDeleteDepartment = async (deptId) => {
    if (!window.confirm("Delete this department?")) return;
    try {
      await deleteDepartment(deptId);
      toast.success("Department deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete department");
    }
  };

  // Designation handlers
  const handleCreateDesignation = async (e) => {
    e.preventDefault();
    try {
      if (editingDesgId) {
        await updateDesignation(editingDesgId, { name: desgFormData.name });
        toast.success("Designation updated");
      } else {
        await createDesignation({ name: desgFormData.name });
        toast.success("Designation created");
      }
      setDesgDialogOpen(false);
      setDesgFormData({ name: "" });
      setEditingDesgId(null);
    } catch {
      toast.error("Failed to save designation");
    }
  };

  const handleEditDesignation = (desg) => {
    setDesgFormData({ name: desg.name });
    setEditingDesgId(desg.id);
    setDesgDialogOpen(true);
  };

  const handleDeleteDesignation = async (desgId) => {
    if (!window.confirm("Delete this designation?")) return;
    try {
      await deleteDesignation(desgId);
      toast.success("Designation deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete designation");
    }
  };

  // Import/Export handlers
  const handleImport = async (file) => {
    setImporting(true);
    setImportResult(null);
    try {
      const result = await importCSV(file);
      setImportResult(result);
      if (result.imported > 0) {
        toast.success(`Imported ${result.imported} employees`);
      }
    } catch (error) {
      setImportResult({ success: false, imported: 0, errors: [error.message] });
      toast.error("Import failed");
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportEmployees(exportFormat, exportStatus);
      toast.success("Export started");
      setExportDialogOpen(false);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  // Login handlers
  const openCreateLogin = (employee) => {
    if (!employee.phone) {
      toast.error("Employee needs a phone number first. Edit the employee to add one.");
      return;
    }
    setLoginEmployee(employee);
    setLoginPassword("");
    setLoginDialogOpen(true);
  };

  const handleCreateLogin = async () => {
    if (!loginPassword || loginPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setCreatingLogin(true);
    try {
      const res = await createEmployeeLogin(loginEmployee.id, loginPassword, loginEmployee.phone);
      toast.success(res.message);
      setLoginDialogOpen(false);
      setLoginEmployee(null);
      setLoginPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create login");
    } finally {
      setCreatingLogin(false);
    }
  };

  const openResetPassword = (employee) => {
    setResetPasswordEmployee(employee);
    setNewResetPassword("");
    setResetPasswordDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!newResetPassword || newResetPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setResettingPassword(true);
    try {
      await resetEmployeePassword(resetPasswordEmployee.user_id, newResetPassword);
      toast.success("Password reset successfully");
      setResetPasswordDialogOpen(false);
      setResetPasswordEmployee(null);
      setNewResetPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(false);
    }
  };

  if (!currentBusiness) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
        <Building2 className="h-16 w-16 text-slate-300 mb-4" />
        <h2 className="text-2xl font-semibold text-slate-800 font-heading mb-2">No Business Selected</h2>
        <p className="text-slate-500">Please select a business to manage employees</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 font-heading">Employees</h1>
          <p className="text-slate-500">Manage your team members</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="rounded-full" onClick={() => setImportDialogOpen(true)} data-testid="import-btn">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Button variant="outline" className="rounded-full" onClick={() => setExportDialogOpen(true)} data-testid="export-btn">
            <Download className="h-4 w-4 mr-2" /> Export
          </Button>
          <DepartmentButton onClick={() => { setEditingDeptId(null); setDeptFormData({ name: "", description: "", approver_id: "" }); setDeptDialogOpen(true); }} />
          <DesignationButton onClick={() => { setEditingDesgId(null); setDesgFormData({ name: "" }); setDesgDialogOpen(true); }} />
          <Button className="btn-primary" onClick={() => setDialogOpen(true)} data-testid="add-employee-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Employee
          </Button>
        </div>
      </div>

      {/* Stats */}
      <EmployeeStats stats={stats} departments={departments} designations={designations} />

      {/* Departments & Designations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="card-soft">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Departments</CardTitle>
          </CardHeader>
          <CardContent>
            {departments.length === 0 ? (
              <p className="text-sm text-slate-500">No departments yet</p>
            ) : (
              <div className="space-y-2">
                {departments.map((dept) => (
                  <div key={dept.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm">{dept.name}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditDepartment(dept)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteDepartment(dept.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-soft">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-slate-600">Designations</CardTitle>
          </CardHeader>
          <CardContent>
            {designations.length === 0 ? (
              <p className="text-sm text-slate-500">No designations yet</p>
            ) : (
              <div className="space-y-2">
                {designations.map((desg) => (
                  <div key={desg.id} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                    <span className="text-sm">{desg.name}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleEditDesignation(desg)}>
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeleteDesignation(desg.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Employee List */}
      <Card className="card-soft">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <CardTitle className="font-heading text-lg">Employee Directory</CardTitle>
            <EmployeeFilters searchTerm={searchTerm} onSearchChange={setSearchTerm} />
          </div>
        </CardHeader>
        <CardContent>
          <EmployeeTable
            employees={filteredEmployees}
            loading={loading}
            getDepartmentName={getDepartmentName}
            getDesignationName={getDesignationName}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onCreateLogin={openCreateLogin}
            onResetPassword={openResetPassword}
            currency={currentBusiness?.currency}
          />
        </CardContent>
      </Card>

      {/* Dialogs */}
      <EmployeeForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        editingEmployee={editingEmployee}
        departments={departments}
        designations={designations}
        employees={employees}
        resetForm={resetForm}
      />

      <DepartmentDialog
        open={deptDialogOpen}
        onOpenChange={setDeptDialogOpen}
        formData={deptFormData}
        setFormData={setDeptFormData}
        onSubmit={handleCreateDepartment}
        editingId={editingDeptId}
        employees={employees}
        onClose={() => { setEditingDeptId(null); setDeptFormData({ name: "", description: "", approver_id: "" }); }}
      />

      <DesignationDialog
        open={desgDialogOpen}
        onOpenChange={setDesgDialogOpen}
        formData={desgFormData}
        setFormData={setDesgFormData}
        onSubmit={handleCreateDesignation}
        editingId={editingDesgId}
        onClose={() => { setEditingDesgId(null); setDesgFormData({ name: "" }); }}
      />

      <ImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onImport={handleImport}
        onDownloadTemplate={downloadTemplate}
        importing={importing}
        importResult={importResult}
        setImportResult={setImportResult}
      />

      <ExportDialog
        open={exportDialogOpen}
        onOpenChange={setExportDialogOpen}
        format={exportFormat}
        setFormat={setExportFormat}
        status={exportStatus}
        setStatus={setExportStatus}
        onExport={handleExport}
        exporting={exporting}
      />

      <LoginDialog
        open={loginDialogOpen}
        onOpenChange={setLoginDialogOpen}
        employee={loginEmployee}
        password={loginPassword}
        setPassword={setLoginPassword}
        onCreate={handleCreateLogin}
        creating={creatingLogin}
      />

      <ResetPasswordDialog
        open={resetPasswordDialogOpen}
        onOpenChange={setResetPasswordDialogOpen}
        employee={resetPasswordEmployee}
        password={newResetPassword}
        setPassword={setNewResetPassword}
        onReset={handleResetPassword}
        resetting={resettingPassword}
      />
    </div>
  );
};

export default Employees;
